// Bioluminescent wake for the fast client (R101): plankton flaring behind a
// moving hull and dying over about two seconds, so that speed is legible on
// the map — a fast hull drags a longer scar than a slow one.
//
// THE CONSTRAINT THIS FILE EXISTS TO ENFORCE (ui-map-interface.md §5): a
// wake is a position broadcast. Painting one behind an enemy contact would
// draw a track through exactly the fog of war the server spends effort
// withholding — worse than the contact blip, because a blip is a bearing
// and a wake is a course. So the gate lives in `wakeVisible()`, every flare
// in the game goes through it, and `wake.test.ts` fails if that ever stops
// being true.
//
// `WakeEmitter` and `wakeVisible` were COPIED from the wake layer of before
// R505 rather than imported: that module built its sprites with a renderer,
// and one import of it would have put the library back into this bundle
// (`nopixi.test.ts`). The particle bookkeeping is unchanged, line for line,
// and this file's own test pins the gate again on the copy.
//
// The sprites become quads of the atlas `wake` cell, tinted `WAKE_COLOR`,
// capped at `WAKE_MAX` and culled to the frame's rect.
//
// Under the light identity (ui-map-interface.md §7, R3294) a flare is a
// covered ink, not light: `WAKE_COLOR` is the light ink of its role, and
// the alpha takes `AP_GLOW_K`, the glow factor of §2.5. In dark the factor
// is 1, so the flare is the flare of before, bit for bit.

import { mulberry32 } from '../../world';
import { CULL_MARGIN_PX, outside, type DrawCtx, type FrameInfo, type Layer, type ViewRect } from '../layer';
// The figures live in data/presentation/presentation.toml (R101 invented
// them, R312 moved them): spawn is distance-based rather than time-based —
// at twice the speed you get twice as many flares over twice the distance
// in the same lifetime, so the scar is literally as long as the hull is
// fast. The colour is the plankton's, not the faction's: what glows is the
// sea reacting to a hull passing.
import {
  WAKE_ALPHA, WAKE_COLOR, WAKE_FULL_SPEED, WAKE_JITTER_PX, WAKE_LIFE_SEC,
  WAKE_MAX, WAKE_MIN_SPEED, WAKE_SIZE_PX, WAKE_SPAWN_PX,
} from '../../gen/presentation';
import type { AtlasEntry } from '../gfx/atlas';
import { AP_GLOW_K } from '../shaders/identity';
import type { BatchSource, QuadSink } from './batches';

export { WAKE_LIFE_SEC, WAKE_SPAWN_PX, WAKE_MIN_SPEED, WAKE_MAX };

/** A fleet as the wake reads it — the fields `FleetLayer.info()` publishes. */
export interface WakeSource {
  id: number;
  faction: number;
  x: number;
  y: number;
  /** Rendered speed in px/s. */
  speed: number;
  /** The server stripped this contact's composition: it is not ours. */
  classified: boolean;
}

/**
 * The fog-of-war gate. Own faction only, and nothing else in this file may
 * spawn a flare without asking.
 *
 * `ownFaction` is 0 before the hello lands, which is not "faction 0" but
 * "we do not know yet" — so it draws nothing at all rather than
 * everything. `classified` is the belt to the faction check's braces: it
 * repeats the fleet layer's own test for a contact whose composition the
 * server withheld, so if a future change ever lets an enemy hull through
 * the faction comparison, it still cannot leave a track.
 *
 * `allFactions` is the replay film's one exception (R531). A film plays a
 * window of the combat log the server already handed over, so every hull
 * in it is a hull this player is allowed to see, and no course it draws
 * can tell anybody anything about the world running now. Nothing on the
 * live map may ever set it: `classified` still refuses, so the flag opens
 * the faction test alone.
 */
export function wakeVisible(
  f: WakeSource, ownFaction: number, allFactions = false,
): boolean {
  if (f.classified) return false;
  if (allFactions) return true;
  return ownFaction > 0 && f.faction === ownFaction;
}

export interface Flare {
  x: number;
  y: number;
  age: number;
  scale: number;
}

/** One hull's line of the ledger: its carried travel and its last step. */
interface Carry {
  d: number;
  seen: number;
}

/**
 * The particle bookkeeping, with no renderer in it: what spawns, where, and
 * when it dies. Split out from the drawing so the faction gate can be
 * tested without a GPU — the test is the point of the split.
 */
export class WakeEmitter {
  readonly flares: Flare[] = [];
  /**
   * The most flares alive at once. `WAKE_MAX` is the designed figure and the
   * batch's capacity; the `waken` knob scales it down for a measurement
   * (R499, R504) and never above the capacity.
   */
  cap = WAKE_MAX;
  /** The replay film's gate opener (R531). See `wakeVisible`. */
  allFactions = false;
  /**
   * Travel carried over between frames, per fleet id, with the step that
   * last saw the hull. A step drops every entry it did not see, so a hull
   * that sank leaves the ledger on the next step.
   */
  private carry = new Map<number, Carry>();
  private rand = mulberry32(0x5ea);
  /** The count of `step` calls, the stamp of the ledger. */
  private stamp = 0;
  /** Drops one ledger entry the step did not see. Bound once (§10.3). */
  private readonly sweep = (c: Carry, id: number): void => {
    if (c.seen !== this.stamp) this.carry.delete(id);
  };

  /** How many hulls the ledger holds. The tests read it. */
  get carried(): number { return this.carry.size; }

  /**
   * Advances one frame. Sources that fail `wakeVisible` are not touched.
   *
   * `view` is the visible world rect, and a hull outside it plus the margin
   * spawns nothing (R192, §10.7). The fog-of-war gate runs first and the
   * culling second, so the culling can never widen what the layer shows.
   * `null` means "no rect", which is how the test drives the emitter with
   * no camera; the layer always hands one over.
   */
  step(
    dt: number, sources: readonly WakeSource[], ownFaction: number,
    view: ViewRect | null = null,
  ): void {
    const x0 = view ? view.x0 - CULL_MARGIN_PX : 0;
    const x1 = view ? view.x1 + CULL_MARGIN_PX : 0;
    const y0 = view ? view.y0 - CULL_MARGIN_PX : 0;
    const y1 = view ? view.y1 + CULL_MARGIN_PX : 0;
    const stamp = ++this.stamp;
    for (const s of sources) {
      if (!wakeVisible(s, ownFaction, this.allFactions)) {
        // Not ours: forget it entirely rather than keeping a distance
        // ledger on a hull we are not allowed to draw.
        this.carry.delete(s.id);
        continue;
      }
      // Off screen: no flare is spawned, and the hull keeps its distance
      // ledger so it does not start a scar from zero when it comes back.
      const c = this.carry.get(s.id);
      if (c) c.seen = stamp;
      if (view && outside(s.x, s.y, x0, y0, x1, y1)) continue;
      if (s.speed < WAKE_MIN_SPEED) continue;
      let d = (c ? c.d : 0) + s.speed * dt;
      const size = Math.min(1, s.speed / WAKE_FULL_SPEED);
      while (d >= WAKE_SPAWN_PX) {
        d -= WAKE_SPAWN_PX;
        if (this.flares.length >= this.cap) { d = 0; break; }
        this.flares.push({
          x: s.x + (this.rand() - 0.5) * 2 * WAKE_JITTER_PX,
          y: s.y + (this.rand() - 0.5) * 2 * WAKE_JITTER_PX,
          age: 0,
          scale: 0.5 + 0.6 * size + this.rand() * 0.25,
        });
      }
      if (c) c.d = d;
      else this.carry.set(s.id, { d, seen: stamp });
    }
    // The live flares close up in place, in their own order, so a death
    // allocates nothing (§10.4).
    const flares = this.flares;
    let live = 0;
    for (let i = 0; i < flares.length; i++) {
      const f = flares[i];
      f.age += dt;
      if (f.age < WAKE_LIFE_SEC) flares[live++] = f;
    }
    flares.length = live;
    // A fleet that sank or sailed out of the picture must not leak a map
    // entry per id for the rest of the session. A count alone cannot see a
    // dead id while new hulls keep the list long, so the stamp decides.
    if (this.carry.size > 0) this.carry.forEach(this.sweep);
  }
}

/**
 * The quads. One `wake` cell per flare on screen, tinted once and faded per
 * frame. A flare outside the rect plus the margin still ages and still dies
 * on time — the water off screen must not stop and then jump — but it packs
 * no instance, so it costs no position, no alpha, no scale and no draw.
 */
export class WakeLayer implements Layer {
  readonly name = 'wake';

  private readonly emitter = new WakeEmitter();
  private readonly quads: QuadSink;
  private readonly cell: AtlasEntry;
  private shown = true;

  private ownFaction = 0;
  private sources: readonly WakeSource[] = [];

  constructor(src: BatchSource) {
    this.quads = src.newQuads(WAKE_MAX);
    this.cell = src.sprite('wake');
  }

  /**
   * The replay film's gate opener (R531), forwarded to the emitter that
   * holds the rule. Nothing on the live map sets it.
   */
  get allFactions(): boolean { return this.emitter.allFactions; }
  set allFactions(v: boolean) { this.emitter.allFactions = v; }

  /** The `waken` knob: a scale on the flare cap, clamped to the batch. */
  setCap(scale: number): void {
    const n = Math.round(WAKE_MAX * scale);
    this.emitter.cap = Math.max(0, Math.min(WAKE_MAX, Number.isFinite(n) ? n : WAKE_MAX));
  }

  /** The fleet snapshot this frame. The array is kept, never copied. */
  feed(sources: readonly WakeSource[], ownFaction: number): void {
    this.sources = sources;
    this.ownFaction = ownFaction;
  }

  update(frame: FrameInfo): void {
    this.emitter.step(frame.dtMs / 1000, this.sources, this.ownFaction, frame.view);
    this.pack(frame.cull);
  }

  draw(ctx: DrawCtx): void {
    this.quads.flush(ctx.cam);
  }

  setVisible(v: boolean): void { this.shown = v; }
  get visible(): boolean { return this.shown; }

  /** How many flares are alive. The tests read it. */
  get count(): number { return this.emitter.flares.length; }

  private pack(cull: ViewRect): void {
    const q = this.quads;
    q.begin();
    for (const f of this.emitter.flares) {
      if (outside(f.x, f.y, cull.x0, cull.y0, cull.x1, cull.y1)) continue;
      // Flare up fast, die slow: the leading edge of the scar is the bright
      // one, which is what makes it read as a direction.
      const t = f.age / WAKE_LIFE_SEC;
      const glow = t < 0.12 ? t / 0.12 : (1 - t) / 0.88;
      // The `wake` cell is the 32 px radial dot the PixiJS layer draws into
      // its texture, so the sprite scale is the same number it sets there.
      const scale = (f.scale * WAKE_SIZE_PX / this.cell.w) * (0.6 + 0.4 * glow);
      q.sprite(f.x, f.y, 0, this.cell, WAKE_COLOR, WAKE_ALPHA * glow * AP_GLOW_K, scale);
    }
  }
}
